import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useAdvertiserStore } from './advertiser.stores'
import { useUserStore } from './user.stores'

import type { AdvertiserItem, AdvertiserType } from '@/lib/types'
import { useEntrypointStore } from '@/stores/entrypoint.stores'

type AdvertiserListItemType = Pick<AdvertiserItem, 'id' | 'logoUrl' | 'name'>
export const useAdvertisersListStore = defineStore('advertisersList', () => {
  const userStore = useUserStore()
  const advertiserStore = useAdvertiserStore()
  const entrypointStore = useEntrypointStore()

  const tempAdvertisers = ref<AdvertiserListItemType[]>([])
  const currentId = ref<number | null>(null)

  const list = computed(() => [...userStore.userAdvertisers, ...tempAdvertisers.value])
  const currentAdvertiser = computed(() =>
    list.value.find((advertiser) => advertiser.id === currentId.value),
  )

  const addTemporaryAdvertiser = (advertiser: AdvertiserListItemType) => {
    tempAdvertisers.value.push(advertiser)
    currentId.value = advertiser.id
  }

  const selectAdvertiser = async (id: number) => {
    currentId.value = id
    await Promise.all([advertiserStore.fetchAdvertiser(id), entrypointStore.fetchConfig(id)])
  }

  const selectInitialAdvertiser = (id: number) => {
    currentId.value = id
  }

  watch(
    () => advertiserStore.advertiser,
    (advertiser: AdvertiserType | undefined) => {
      if (advertiser && currentId.value !== advertiser.id) {
        const isNewAdvertiserPresent = list.value.find((ad) => ad.id === advertiser.id)
        if (!isNewAdvertiserPresent) {
          addTemporaryAdvertiser({
            id: advertiser.id,
            logoUrl: advertiser.logoUrl,
            name: advertiser.feedName,
          })
        } else {
          selectInitialAdvertiser(advertiser.id)
        }
      }
    },
    { immediate: true },
  )

  return {
    list,
    currentId,
    currentAdvertiser,
    addTemporaryAdvertiser,
    selectInitialAdvertiser,
    selectAdvertiser,
  }
})
